import { useQuery } from "@connectrpc/connect-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { OperatorService, type Schedule } from "@yatayat/gen/yatayat/operator/v1/operator_pb";

import { FleetAlerts } from "~/components/FleetAlerts";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Loading, QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { localDigits, vehicleClassLabels, weekdayLabels } from "~/lib/labels";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type SchedulesSearch = { saved?: string; kept?: number };

// What was just saved rides in the URL, so the notice survives the redirect from the form.
export const Route = createFileRoute("/operator/schedules/")({
  validateSearch: (search: Record<string, unknown>): SchedulesSearch => ({
    saved: searchString(search.saved),
    kept: typeof search.kept === "number" && search.kept > 0 ? search.kept : undefined,
  }),
  component: Schedules,
});

function Schedules() {
  const { t, lang } = useI18n();
  const { saved, kept } = Route.useSearch();
  const schedules = useQuery(OperatorService.method.listSchedules, {}, { retry: retryTransient });
  const unauthenticated = useStaffSessionExpiry(schedules.error);

  const byRoute = new Map<string, Schedule[]>();
  for (const s of schedules.data?.schedules ?? []) {
    byRoute.set(s.routeCode, [...(byRoute.get(s.routeCode) ?? []), s]);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className={ui.h1}>{t("schedulesTitle")}</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("schedulesIntro")}</p>
        </div>
        <Link
          to="/operator/schedules/new"
          className="rounded-md bg-brand px-4 py-2 font-medium text-white dark:bg-brand-dark"
        >
          {t("addSchedule")}
        </Link>
      </div>

      <div role="status">
        {saved && (
          <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-900 dark:bg-green-900/30 dark:text-green-200">
            {t("scheduleSaved")}
            {kept !== undefined && ` ${kept === 1 ? t("scheduleTripKept") : t("scheduleTripsKept", { n: localDigits(String(kept), lang) })}`}
          </p>
        )}
      </div>

      <FleetAlerts />

      {schedules.isPending && <Loading />}
      {schedules.isError && !schedules.data && !unauthenticated && (
        <QueryError onRetry={() => void schedules.refetch()} busy={schedules.isFetching} />
      )}
      {schedules.data?.schedules.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-600 dark:border-slate-700 dark:text-slate-400">
          {t("noSchedules")}
        </p>
      )}

      {[...byRoute.entries()].map(([code, list]) => (
        <section key={code} aria-labelledby={`route-${code}`} className="space-y-2">
          <h2 id={`route-${code}`} className="text-lg font-semibold">
            {list[0]?.routeName?.[lang] || code}
          </h2>
          <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
            {list.map((s) => (
              <ScheduleRow key={s.id} schedule={s} highlight={s.id === saved} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ScheduleRow({ schedule: s, highlight }: { schedule: Schedule; highlight: boolean }) {
  const { t, lang } = useI18n();
  const days = s.daysOfWeek.length === 7 ? t("everyDay") : s.daysOfWeek.map((d) => t(weekdayLabels[d] ?? "daySun")).join(", ");

  return (
    <li className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 ${highlight ? "bg-green-50/70 dark:bg-green-900/20" : ""}`}>
      <span className="w-14 text-xl font-semibold tabular-nums text-ink dark:text-ink-dark">{localDigits(s.departsAt, lang)}</span>
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium">{days}</p>
        <p className="text-slate-600 dark:text-slate-400">
          {/* Kept as registered, since it must match the plate on the bus. */}
          {s.plateNo ? <span className="font-mono text-slate-800 dark:text-slate-200">{s.plateNo}</span> : t("noBus")}
          {` · ${t(vehicleClassLabels[s.vehicleClass])}`}
          {s.electric && ` · ${t("electric")}`}
        </p>
      </div>
      {s.paused && (
        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-200">
          {t("schedulePaused")}
        </span>
      )}
      <Link
        to="/operator/schedules/$scheduleId"
        params={{ scheduleId: s.id }}
        className="text-sm font-medium text-ink underline underline-offset-2 dark:text-ink-dark"
      >
        {t("editSchedule")}
        <span className="sr-only"> {localDigits(s.departsAt, lang)}</span>
      </Link>
    </li>
  );
}
